import { ErrorCodes } from '@aline/common';
import { namedOperations } from 'src/gql/generated/apollo-types';

/**
 * Operations whose failures the surface renders itself, so the generic error UI
 * would only duplicate what the user already sees. The chat pair is here
 * because both select the same `agentThread` field from separate documents, so
 * Apollo cannot dedupe them and one missing thread produced two identical
 * toasts.
 *
 * A match here skips `errorLink` entirely for the codes listed in
 * `SURFACE_HANDLED_ERROR_CODES` — no toast, and no `handleError`, so no Sentry
 * report either. Every other code still runs the normal path, because a surface
 * that renders its own not-found has no answer for an outage.
 */
const IGNORED_OPERATIONS: ReadonlySet<string> = new Set([
  namedOperations.Query.GetRequesterDocumentAccess,
  namedOperations.Query.GetAgentThread,
  namedOperations.Query.GetThreadTitle,
]);

/**
 * The codes an ignored operation's surface renders for itself. Scoped rather
 * than blanket, so an `INTERNAL` on a chat query still reaches the toast and
 * Sentry instead of leaving the user with an empty panel and no explanation.
 */
const SURFACE_HANDLED_ERROR_CODES: ErrorCodes[] = [ErrorCodes.NOT_FOUND, ErrorCodes.UNAUTHORIZED];

/**
 * The caller reports every error from these operations, so the generic toast
 * would only repeat it. `UploadTaskResponseVersion` is here because its
 * no-tracked-changes refusal is a confirm the hook owns. `ConfirmMetadataImport`
 * because the import wizard toasts every failure in its own `catch`.
 *
 * Session redirects still run. The caller does not own those.
 */
const SELF_REPORTED_OPERATIONS: ReadonlySet<string> = new Set([
  namedOperations.Mutation.ConfirmMetadataImport,
  namedOperations.Mutation.UploadTaskResponseVersion,
]);

const SESSION_ERROR_CODES: ErrorCodes[] = [
  ErrorCodes.UNAUTHENTICATED,
  ErrorCodes.USER_DEACTIVATED_ERROR,
];

export function getIsIgnoredOperation(operationName: string | undefined): boolean {
  return Boolean(operationName && IGNORED_OPERATIONS.has(operationName));
}

export function getIsSelfReportedOperation(operationName: string | undefined): boolean {
  return Boolean(operationName && SELF_REPORTED_OPERATIONS.has(operationName));
}

/**
 * Whether `errorLink` should act on an error, or leave it to the surface that
 * raised it. An ignored operation leaves only the codes its surface renders.
 * A self-reported operation leaves every code except the session redirects.
 */
export function getShouldHandleError({
  code,
  isIgnoredOperation,
  isSelfReportedOperation,
}: {
  code: ErrorCodes | void;
  isIgnoredOperation: boolean;
  isSelfReportedOperation: boolean;
}): boolean {
  if (isSelfReportedOperation) return Boolean(code && SESSION_ERROR_CODES.includes(code));
  if (!isIgnoredOperation || !code) return true;

  return !SURFACE_HANDLED_ERROR_CODES.includes(code);
}
